import React, { useEffect, useMemo } from 'react';
import { PageHeader } from '../../PageHeader';
import { OrgSelect } from '../../shared/OrgSelect';
import { useApi } from '../../useApi';
import { HelpVideo } from '../../../enums/helpVideo';
import { ExportButton } from '../../shared/ExportButton';
import { ReportLogTable } from './ReportLogTable';

import { useAuth } from "../../../features/auth/context";
import { useOrgSiteContext } from "../../../hooks/useOrgSiteContext";
import { EventTypeFilterFormObj, useLogFilterRequest } from "../../../hooks/useLogFilterRequest";
import { LogFilterRequestSection } from "../../LogFilterRequestSection";

import { DEFAULT_EVENT_TYPE_NAME_VALUE, EVENT_TYPE_NAME_OPTIONS } from "./ManagementEventOptions";
import { useEvents } from "./useEvents";

export const REPORT_EVENT_TYPES_STORAGE_KEY = "ssa.reportLog.eventTypes";

function getStoredEventTypes(): EventTypeFilterFormObj[] | undefined {
    try {
        const raw = sessionStorage.getItem(REPORT_EVENT_TYPES_STORAGE_KEY);
        return raw ? JSON.parse(raw) : undefined;
    } catch {
        return undefined;
    }
}

export function ReportLogPage() {
    const {
        isOrgAdmin,
        isOrgEmployee,
        isSiteAdmin,
        isSiteEmployee,
    } = useAuth();
    const { eventApi } = useApi();
    const initialEventTypes = useMemo(
        () => getStoredEventTypes() ?? DEFAULT_EVENT_TYPE_NAME_VALUE,
        []
    );
    const { formObj, setFormObj, request, resetFormObj } = useLogFilterRequest({ eventTypes: initialEventTypes });

    useEffect(() => {
        if (formObj.eventTypes) {
            sessionStorage.setItem(REPORT_EVENT_TYPES_STORAGE_KEY, JSON.stringify(formObj.eventTypes));
        }
    }, [formObj.eventTypes]);
    const { dealerId, siteId, orgId, siteChangeCallback } = useOrgSiteContext("reports", formObj);
    const { events, isLoading, numberOfPages } = useEvents(dealerId, orgId, siteId, request);

    const deassocciateSiteFromContext = isOrgAdmin || isOrgEmployee || isSiteAdmin || isSiteEmployee;

    const reportRequest = useMemo(() => {
        let apiMethod = () => eventApi.getEventReport(request);
        if (dealerId && orgId === undefined && siteId === undefined) {
            apiMethod = () => eventApi.getEventReportForDealer(dealerId, request);
        } else if (orgId && siteId === undefined) {
            apiMethod = () => eventApi.getEventReportForOrganization(orgId, request);
        } else if (orgId && siteId) {
            apiMethod = () => eventApi.getEventReportForSite(orgId, siteId, request);
        }
        return apiMethod;
    }, [dealerId, orgId, siteId, request, eventApi]);

    return (
        <>
            <PageHeader
                helpVideoNum={HelpVideo.Report}
                isLoading={isLoading}
            >
                Reports
            </PageHeader>

            <OrgSelect
                selectedDealerId={dealerId}
                selectedOrgId={orgId}
                selectedSiteId={siteId || 0}
                includeAllSites
                bypassSiteContext={deassocciateSiteFromContext}
                onChange={(val: {
                    dealerId?: number | null;
                    orgId?: number | null;
                    siteId?: number | null;
                }) => siteChangeCallback(val.dealerId, val.orgId, val.siteId)}
            />

            <LogFilterRequestSection
                formObj={formObj}
                setFormObj={setFormObj}
                resetFormObj={resetFormObj}
                dealerId={dealerId}
                organizationId={orgId}
                siteId={siteId}
                logTypeOptions={EVENT_TYPE_NAME_OPTIONS}
                numberOfPages={numberOfPages}
                buttons={
                    <ExportButton
                        apiRequest={reportRequest}
                        targetLabel="Event Report"
                        buttonLabel="Export Report"
                    />
                }
                paginationDisabled={isLoading}
                allowMultipleEventTypes={true}
            />

            <ReportLogTable
                events={events}
                filterByLocalTime={formObj.filterByLocalTime}
                isLoading={isLoading}
            />
        </>
    );
};
